import { useCallback, useEffect, useRef } from "react";
import { useAuthStore } from "../../../auth/auth-store.js";
import { sameSession, scopeFromClaims } from "../../../auth/session-scope.js";

/**
 * Whether an export download's answer may still be used when it arrives.
 *
 * A grant request outlives both the Audit page that sent it and the session
 * that asked: the admin can leave the page, or another administrator or a new
 * session can replace this one in place, while it is in flight. The answer is a
 * storage URL valid for minutes, and the server checked the session only when
 * it issued it. Opening it is a top-level navigation, so a late answer must be
 * dropped: never downloaded on another page or for another session.
 *
 * Call the hook once per component, and call the returned `begin` as the
 * request starts. `begin` captures the session that asked; what it returns
 * says, at any later moment, whether this page is still mounted and that
 * session is still the signed-in one. The page's MOUNT is what counts, not one
 * effect run: the link handler's effect re-runs when it strips `export`, and
 * that must not cancel the download it started.
 */
export function useDownloadGuard(): () => () => boolean {
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return useCallback(() => {
    const asked = scopeFromClaims(useAuthStore.getState().claims);
    return () =>
      mounted.current &&
      sameSession(asked, scopeFromClaims(useAuthStore.getState().claims));
  }, []);
}
